/**
 * Dropdown — a small, accessible, anchored popover used by the top bar
 * (notifications, profile).
 *
 * Behaviour (standard SaaS menu):
 *   - A trigger button toggles an anchored panel positioned to its right edge.
 *   - Closes on outside pointer-down, on Escape, and on selecting an item.
 *   - `aria-haspopup` / `aria-expanded` on the trigger; the panel is `role`d by
 *     the caller (menu / region). Focus returns to the trigger on Escape.
 *
 * SSR-safe: all DOM/global access (document listeners) is inside effects, so the
 * server render is a pure trigger button with the panel absent.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

/** Props for {@link Dropdown}. */
export interface DropdownProps {
  /** Accessible label for the trigger button. */
  readonly label: string;
  /** Trigger contents (icon, avatar, etc.). */
  readonly trigger: ReactNode;
  /** Panel contents (rendered only when open). */
  readonly children: ReactNode;
  /** Extra class on the trigger button. */
  readonly triggerClassName?: string;
  /** Extra class on the panel. */
  readonly panelClassName?: string;
  /** Optional role for the panel (defaults to "menu"). */
  readonly panelRole?: "menu" | "region" | "dialog";
}

/**
 * A click-to-open popover anchored under its trigger, with outside-click and
 * Escape dismissal and focus return.
 */
export function Dropdown({
  label,
  trigger,
  children,
  triggerClassName,
  panelClassName,
  panelRole = "menu",
}: DropdownProps): ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  const close = useCallback((): void => setOpen(false), []);

  // Outside pointer-down closes the panel.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      const root = rootRef.current;
      if (root !== null && !root.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // Escape closes and returns focus to the trigger.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <div className="console-dropdown" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={
          triggerClassName ?? "console-iconbtn"
        }
        aria-haspopup={panelRole === "region" ? true : "menu"}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      {open ? (
        <div
          id={panelId}
          role={panelRole}
          aria-label={label}
          className={
            panelClassName === undefined
              ? "console-dropdown__panel"
              : `console-dropdown__panel ${panelClassName}`
          }
        >
          {/* Close the panel when an actionable item inside is activated. */}
          <div
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest("[data-dropdown-dismiss]") !== null) {
                close();
              }
            }}
          >
            {children}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Dropdown;
